<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const emit = defineEmits(['submit']);
const { t } = useI18n();

const dialogRef = ref(null);
const conversationContext = ref(null);
const selectedCategory = ref('');
const detailText = ref('');

const categoryOptions = computed(() => [
  {
    value: t('RESOLVE_REASON.CATEGORIES.PROBLEM_SOLVED'),
    label: t('RESOLVE_REASON.CATEGORIES.PROBLEM_SOLVED'),
  },
  {
    value: t('RESOLVE_REASON.CATEGORIES.SALE_MADE'),
    label: t('RESOLVE_REASON.CATEGORIES.SALE_MADE'),
  },
  {
    value: t('RESOLVE_REASON.CATEGORIES.NO_RESPONSE'),
    label: t('RESOLVE_REASON.CATEGORIES.NO_RESPONSE'),
  },
  {
    value: t('RESOLVE_REASON.CATEGORIES.SYSTEM_CLOSED'),
    label: t('RESOLVE_REASON.CATEGORIES.SYSTEM_CLOSED'),
  },
  {
    value: t('RESOLVE_REASON.CATEGORIES.SPAM'),
    label: t('RESOLVE_REASON.CATEGORIES.SPAM'),
  },
  {
    value: t('RESOLVE_REASON.CATEGORIES.OTHER'),
    label: t('RESOLVE_REASON.CATEGORIES.OTHER'),
  },
]);

const canSubmit = computed(
  () => selectedCategory.value.trim() !== '' || detailText.value.trim() !== ''
);

const buildReason = () => {
  const category = selectedCategory.value.trim();
  const detail = detailText.value.trim();
  if (category && detail) return `${category} — ${detail}`;
  return category || detail;
};

const open = (context = null) => {
  conversationContext.value = context;
  selectedCategory.value = '';
  detailText.value = '';
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
  conversationContext.value = null;
};

const handleConfirm = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    reason: buildReason(),
    context: conversationContext.value,
  });
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :title="t('RESOLVE_REASON.MODAL.TITLE')"
    :description="t('RESOLVE_REASON.MODAL.DESCRIPTION')"
    :confirm-button-label="t('RESOLVE_REASON.MODAL.SUBMIT')"
    :cancel-button-label="t('RESOLVE_REASON.MODAL.CANCEL')"
    :disable-confirm-button="!canSubmit"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('RESOLVE_REASON.MODAL.CATEGORY_LABEL') }}
        </label>
        <ComboBox
          v-model="selectedCategory"
          :options="categoryOptions"
          :placeholder="t('RESOLVE_REASON.MODAL.CATEGORY_PLACEHOLDER')"
          class="w-full"
        />
      </div>
      <div class="flex flex-col gap-2">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('RESOLVE_REASON.MODAL.DETAIL_LABEL') }}
        </label>
        <TextArea
          v-model="detailText"
          class="w-full"
          :placeholder="t('RESOLVE_REASON.MODAL.DETAIL_PLACEHOLDER')"
        />
      </div>
    </div>
  </Dialog>
</template>
